/* PhraseEditor — CRUD modal for managing text-expansion snippets.
 *
 * Each snippet: { id, trigger, expansion, enabled, used_count?, last_used? }
 * Triggers expand when typed followed by space/tab/enter in any input on the page.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Sparkles, Zap, ZapOff, Search, Edit3, Save, Wand2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  loadPhrases, savePhrases, newPhraseId, expandVariables,
} from '../utils/phraseSnippets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENABLED_KEY = 'phrases_enabled_v1';

const VARIABLE_TIPS = [
  { token: '{{date}}',     desc: 'Dagens datum' },
  { token: '{{time}}',     desc: 'Aktuell tid' },
  { token: '{{datetime}}', desc: 'Datum + tid' },
  { token: '{{day}}',      desc: 'Veckodag (Onsdag)' },
  { token: '{{tomorrow}}', desc: 'Morgondagens datum' },
  { token: '{{name}}',     desc: 'Ditt namn' },
];

export default function PhraseEditor({ open, onClose }) {
  const [phrases, setPhrases] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // phrase being edited
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhrases(loadPhrases());
    try {
      setGlobalEnabled(localStorage.getItem(ENABLED_KEY) !== '0');
    } catch {/**/}
  }, [open]);

  const persist = (next) => {
    setPhrases(next);
    savePhrases(next);
  };

  const addPhrase = () => {
    const p = {
      id: newPhraseId(),
      trigger: ';',
      expansion: '',
      enabled: true,
      used_count: 0,
    };
    persist([p, ...phrases]);
    setEditing(p);
  };

  const updatePhrase = (id, patch) => {
    const next = phrases.map((p) => p.id === id ? { ...p, ...patch } : p);
    persist(next);
    if (editing?.id === id) setEditing({ ...editing, ...patch });
  };

  const removePhrase = (id) => {
    if (!window.confirm('Ta bort denna fras?')) return;
    persist(phrases.filter((p) => p.id !== id));
    if (editing?.id === id) setEditing(null);
  };

  const toggleGlobal = () => {
    const next = !globalEnabled;
    setGlobalEnabled(next);
    try { localStorage.setItem(ENABLED_KEY, next ? '1' : '0'); } catch {/**/}
    try { window.dispatchEvent(new CustomEvent('phrases-enabled-changed')); } catch {/**/}
    toast.success(next ? '⚡ Frasersättning PÅ' : '💤 Frasersättning AV');
  };

  // AI-suggestion: give a topic, get a few snippet ideas
  const aiSuggest = async () => {
    if (!aiPrompt.trim()) { toast.info('Skriv ett ämne, t.ex. "kundmejl" 💛'); return; }
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/phrases/suggest`, {
        topic: aiPrompt, count: 5,
      }, { withCredentials: true });
      const suggestions = r.data?.suggestions || [];
      if (!Array.isArray(suggestions) || suggestions.length === 0) throw new Error('Tomma förslag');
      const newPhrases = suggestions.slice(0, 6).map((s) => ({
        id: newPhraseId(),
        trigger: (s.trigger || ';nytt').trim().slice(0, 24),
        expansion: (s.expansion || '').trim().slice(0, 600),
        enabled: true,
        used_count: 0,
      })).filter((p) => p.trigger && p.expansion);
      persist([...newPhrases, ...phrases]);
      setAiPrompt('');
      toast.success(`✨ ${newPhrases.length} nya snippets tillagda`);
    } catch (e) {
      toast.error('Kunde inte föreslå just nu 💔 ' + (e?.message || ''));
    } finally {
      setAiBusy(false);
    }
  };

  if (!open) return null;

  const filtered = phrases.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.trigger || '').toLowerCase().includes(q) ||
           (p.expansion || '').toLowerCase().includes(q);
  });

  return (
    <div className="fixed inset-0 z-[300] bg-black/40 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto" data-testid="phrase-editor">
      <div className="bg-gradient-to-br from-violet-50 via-indigo-50 to-sky-50 rounded-3xl max-w-3xl w-full shadow-2xl border-2 border-violet-200 max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-violet-100 via-indigo-100 to-sky-100 px-5 py-3.5 rounded-t-3xl flex items-center justify-between border-b border-violet-200">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-violet-800 flex items-center gap-2">
              <FileText className="w-5 h-5" /> Fraseditor
            </h2>
            <p className="text-[11px] text-violet-700/70 italic">
              Skriv en trigger + mellanslag — texten expanderas automatiskt ✨
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleGlobal}
              data-testid="phrase-global-toggle"
              className={`px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 transition ${
                globalEnabled ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              title="Slå av/på frasersättning globalt"
            >
              {globalEnabled ? <><Zap className="w-3 h-3" /> PÅ</> : <><ZapOff className="w-3 h-3" /> AV</>}
            </button>
            <button onClick={onClose} data-testid="phrase-editor-close" className="p-2 rounded-full hover:bg-white/60 text-violet-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {/* Top bar — search + add + AI */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-violet-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Sök bland mina fraser…"
                  data-testid="phrase-search"
                  className="w-full pl-9 pr-3 py-2 rounded-xl border-2 border-violet-200 bg-white/90 focus:border-violet-500 outline-none text-sm"
                />
              </div>
              <button
                onClick={addPhrase}
                data-testid="phrase-add"
                className="px-3 py-2 rounded-xl bg-violet-500 hover:bg-violet-600 text-white text-sm font-bold flex items-center gap-1 shadow"
              >
                <Plus className="w-4 h-4" /> Ny
              </button>
            </div>
            <div className="flex gap-2">
              <input
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && aiSuggest()}
                placeholder='✨ AI: säg ett ämne (t.ex. "kundmejl", "vänliga svar")'
                data-testid="phrase-ai-prompt"
                className="flex-1 px-3 py-2 rounded-xl border-2 border-violet-200 bg-white/70 focus:border-violet-500 outline-none text-sm"
              />
              <button
                onClick={aiSuggest}
                disabled={aiBusy}
                data-testid="phrase-ai-suggest"
                className="px-3 py-2 rounded-xl bg-gradient-to-r from-pink-400 to-violet-500 hover:from-pink-500 hover:to-violet-600 text-white text-sm font-bold flex items-center gap-1 shadow disabled:opacity-50"
              >
                <Wand2 className="w-4 h-4" />
                {aiBusy ? 'Funderar…' : 'AI-förslag'}
              </button>
            </div>
          </div>

          {/* List */}
          <div className="space-y-1.5" data-testid="phrase-list">
            {filtered.length === 0 && (
              <p className="text-sm text-violet-600/70 italic text-center py-6">
                Inga fraser hittade. Klicka "Ny" eller låt AI:n föreslå 💛
              </p>
            )}
            {filtered.map((p) => {
              const isEditing = editing?.id === p.id;
              return (
                <div
                  key={p.id}
                  className={`rounded-xl border-2 transition ${
                    isEditing
                      ? 'border-violet-400 bg-white shadow-md'
                      : 'border-violet-100 bg-white/70 hover:bg-white'
                  }`}
                  data-testid={`phrase-row-${p.id}`}
                >
                  {!isEditing && (
                    <div className="flex items-center gap-2 px-3 py-2">
                      <button
                        onClick={() => updatePhrase(p.id, { enabled: !p.enabled })}
                        data-testid={`phrase-toggle-${p.id}`}
                        className={`w-8 h-5 rounded-full relative transition ${p.enabled ? 'bg-emerald-400' : 'bg-gray-300'}`}
                        title={p.enabled ? 'Aktiv — klicka för att stänga av' : 'Avstängd'}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${p.enabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                      </button>
                      <code className="px-2 py-0.5 rounded-md bg-violet-100 text-violet-800 text-xs font-mono font-bold min-w-[60px] inline-block text-center">
                        {p.trigger}
                      </code>
                      <span className="text-violet-300">→</span>
                      <span className="flex-1 text-sm text-gray-700 truncate" title={p.expansion}>
                        {p.expansion}
                      </span>
                      {p.used_count > 0 && (
                        <span className="text-[10px] text-violet-400 font-mono whitespace-nowrap">
                          {p.used_count}×
                        </span>
                      )}
                      <button
                        onClick={() => setEditing(p)}
                        data-testid={`phrase-edit-${p.id}`}
                        className="p-1.5 text-violet-500 hover:bg-violet-100 rounded-lg"
                        title="Redigera"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => removePhrase(p.id)}
                        data-testid={`phrase-delete-${p.id}`}
                        className="p-1.5 text-rose-400 hover:bg-rose-100 rounded-lg"
                        title="Ta bort"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {isEditing && (
                    <div className="p-3 space-y-2" data-testid={`phrase-edit-form-${p.id}`}>
                      <div className="flex gap-2">
                        <div className="w-40">
                          <label className="text-[10px] uppercase font-bold text-violet-600 tracking-wide">Trigger</label>
                          <input
                            value={editing.trigger}
                            onChange={(e) => updatePhrase(p.id, { trigger: e.target.value })}
                            data-testid={`phrase-trigger-input-${p.id}`}
                            placeholder=";hej"
                            className="w-full px-2 py-1.5 rounded-lg border-2 border-violet-200 focus:border-violet-500 outline-none text-sm font-mono"
                            maxLength={24}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-[10px] uppercase font-bold text-violet-600 tracking-wide">Expanderas till</label>
                          <textarea
                            value={editing.expansion}
                            onChange={(e) => updatePhrase(p.id, { expansion: e.target.value })}
                            data-testid={`phrase-expansion-input-${p.id}`}
                            rows={3}
                            placeholder="Texten som ska skrivas ut..."
                            className="w-full px-2 py-1.5 rounded-lg border-2 border-violet-200 focus:border-violet-500 outline-none text-sm resize-y"
                            maxLength={2000}
                          />
                        </div>
                      </div>
                      {/* Preview */}
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs">
                        <span className="text-[10px] uppercase font-bold text-emerald-700 tracking-wide block mb-0.5">Förhandsvisning</span>
                        <pre className="whitespace-pre-wrap font-sans text-emerald-900">{expandVariables(editing.expansion) || '(tom)'}</pre>
                      </div>
                      {/* Variable chips */}
                      <div className="flex flex-wrap gap-1">
                        {VARIABLE_TIPS.map((v) => (
                          <button
                            key={v.token}
                            onClick={() => updatePhrase(p.id, { expansion: (editing.expansion || '') + v.token })}
                            data-testid={`phrase-var-${v.token.replace(/[{}]/g, '')}`}
                            className="text-[10px] px-2 py-1 rounded-full bg-violet-100 hover:bg-violet-200 text-violet-700 font-mono"
                            title={v.desc}
                          >
                            + {v.token}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2 justify-end pt-1">
                        <button
                          onClick={() => setEditing(null)}
                          data-testid={`phrase-done-${p.id}`}
                          className="px-3 py-1.5 rounded-lg bg-violet-500 hover:bg-violet-600 text-white text-xs font-bold flex items-center gap-1"
                        >
                          <Save className="w-3 h-3" /> Klar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer tip */}
          <div className="bg-violet-50 border border-violet-200 rounded-xl px-3 py-2 text-[11px] text-violet-700 flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Så funkar det:</strong> Skriv din trigger (t.ex. <code className="px-1 bg-white rounded">;hej</code>) i vilket textfält som helst, följt av <kbd className="px-1 bg-white rounded text-[10px]">mellanslag</kbd>, <kbd className="px-1 bg-white rounded text-[10px]">Tab</kbd> eller <kbd className="px-1 bg-white rounded text-[10px]">Enter</kbd>. Triggern ersätts direkt med din text. Variabler som <code className="px-1 bg-white rounded">{'{{date}}'}</code> fylls i automatiskt 💛
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
